import React, { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  loadStepPendingToolCallAction,
  selectHasStepPendingToolCallEntry,
  selectStepPendingToolCall,
} from 'store/aiProcessRuns';

import ConditionReviewList from './ConditionReviewList';
import { CONDITION_REVIEW_VARIANT } from './ConditionReviewList/ConditionReviewList.constants';
import FieldChangeList from './FieldChangeList';
import StepPendingCommitChanges from './StepPendingCommitChanges';
import {
  STEP_PENDING_TOOL_CALL_LABELS,
  StepPendingToolCallTestIds,
} from './StepPendingToolCall.constants';
import {
  argumentsStyles,
  containerStyles,
  headlineStyles,
  hintStyles,
} from './StepPendingToolCall.styles';
import { buildStepPendingToolCallView } from './StepPendingToolCall.utils';

const StepPendingToolCall = ({ stepExecutionId, review, conditions: conditionsProp = null }) => {
  const dispatch = useDispatch();
  const pendingToolCall = useSelector(selectStepPendingToolCall(stepExecutionId));
  const hasEntry = useSelector(selectHasStepPendingToolCallEntry(stepExecutionId));

  const view = useMemo(() => buildStepPendingToolCallView(pendingToolCall), [pendingToolCall]);

  // Reload path: a page refresh loses the live SignalR payload, so fetch the
  // pending tool call over REST when the gate is shown and the slice has no
  // entry yet (the live path otherwise wins and short-circuits this).
  useEffectSafeStrictMode(() => {
    if (!hasEntry && stepExecutionId) {
      dispatch(loadStepPendingToolCallAction({ stepExecutionId }));
    }
  }, [dispatch, hasEntry, stepExecutionId]);

  if (!view) {
    return null;
  }

  const { friendlyName, fieldChanges, formattedArguments, actionSummary, isStagedChangesReview } =
    view;
  // The parent resolves library-condition ids to display text before building the
  // review, so prefer the conditions it passes; fall back to the view's own
  // (adhoc) conditions when rendered without a parent-supplied list.
  const conditions = conditionsProp ?? view.conditions;
  const hasConditions = conditions.length > 0;
  const hasFieldChanges = fieldChanges.length > 0;
  const showArguments = !hasConditions && !hasFieldChanges;

  return (
    <Box data-testid={StepPendingToolCallTestIds.CONTAINER} sx={containerStyles}>
      <Typography
        data-testid={StepPendingToolCallTestIds.HEADLINE}
        sx={headlineStyles}
        variant="bold12"
      >
        {`${STEP_PENDING_TOOL_CALL_LABELS.HEADLINE_PREFIX} ${friendlyName}`}
      </Typography>
      {hasConditions && (
        <>
          <Typography
            data-testid={StepPendingToolCallTestIds.CONDITIONS_HINT}
            sx={hintStyles}
            variant="regular12"
          >
            {STEP_PENDING_TOOL_CALL_LABELS.CONDITIONS_HINT({ count: conditions.length })}
          </Typography>
          <ConditionReviewList
            conditions={conditions}
            review={review}
            variant={CONDITION_REVIEW_VARIANT.PROCESS}
          />
        </>
      )}
      {hasFieldChanges && (
        <>
          <Typography
            data-testid={StepPendingToolCallTestIds.CHANGES_HINT}
            sx={hintStyles}
            variant="regular12"
          >
            {STEP_PENDING_TOOL_CALL_LABELS.CHANGES_HINT}
          </Typography>
          <FieldChangeList fieldChanges={fieldChanges} />
        </>
      )}
      {showArguments && (
        <>
          {actionSummary && (
            <Typography
              data-testid={StepPendingToolCallTestIds.ACTION_SUMMARY}
              sx={hintStyles}
              variant="regular12"
            >
              {actionSummary}
            </Typography>
          )}
          {isStagedChangesReview && <StepPendingCommitChanges />}
          {!actionSummary && !isStagedChangesReview && (
            <Box>
              <Typography sx={hintStyles} variant="bold12">
                {STEP_PENDING_TOOL_CALL_LABELS.ARGUMENTS_LABEL}
              </Typography>
              <Typography
                data-testid={StepPendingToolCallTestIds.GENERIC_ARGUMENTS}
                sx={argumentsStyles}
                variant="regular12"
              >
                {formattedArguments ?? STEP_PENDING_TOOL_CALL_LABELS.NO_ARGUMENTS}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

StepPendingToolCall.propTypes = {
  stepExecutionId: PropTypes.string.isRequired,
  review: PropTypes.object.isRequired,
  conditions: PropTypes.arrayOf(PropTypes.object),
};

export default memo(StepPendingToolCall);
